import React, { useState, useEffect } from 'react';
import { getStudents, searchStudents } from '../services/api';
import StudentDetailModal from '../components/StudentDetailModal';
import { useLanguage } from '../LanguageContext';

function StudentsPage() {
  const { t } = useLanguage();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadStudents();
  }, []);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const response = await getStudents();
      setStudents(response.data);
    } catch (error) {
      console.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
    
    if (!query.trim()) {
      loadStudents();
      return;
    }

    setSearching(true);
    try {
      const response = await searchStudents(query);
      setStudents(response.data);
    } catch (error) {
      console.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentStudents = students.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(students.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="card">
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
        <h2>{t('studentApplications')}</h2>
        <div style={{fontSize: '18px', fontWeight: 'bold', color: '#2d5f3f'}}>
          Total: {students.length}
        </div>
      </div>

      <div style={{marginBottom: '20px'}}>
        <input
          type="text"
          placeholder="Search by name, application number, civil ID, contact number..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            outline: 'none'
          }}
        />
      </div>

      {loading || searching ? (
        <div style={{textAlign: 'center', padding: '60px 0'}}>
          <div className="spinner"></div>
          <p style={{marginTop: '20px', color: '#666'}}>Loading...</p>
        </div>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>App No.</th>
                <th>{t('studentName')}</th>
                <th>{t('fatherName')}</th>
                <th>{t('dateOfBirth')}</th>
                <th>{t('area')}</th>
                <th>{t('contactNumber')}</th>
                <th>{t('status')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {currentStudents.map(student => (
                <tr key={student.id}>
                  <td><strong>{student.applicationNumber}</strong></td>
                  <td>{student.studentName}</td>
                  <td>{student.fatherName}</td>
                  <td>{student.dateOfBirth}</td>
                  <td>{student.area} - {student.location}</td>
                  <td>{student.contactNumber}</td>
                  <td>
                    <span className={`status-badge status-${student.status.toLowerCase()}`}>
                      {t(student.status.toLowerCase())}
                    </span>
                  </td>
                  <td>
                    <button className="btn btn-primary" onClick={() => setSelectedStudentId(student.id)}>
                      {t('viewDetails')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px'}}>
              <button 
                className="btn btn-primary" 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                style={{opacity: currentPage === 1 ? 0.5 : 1}}
              >
                Previous
              </button>
              
              <div style={{display: 'flex', gap: '5px'}}>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => paginate(index + 1)}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #2d5f3f',
                      backgroundColor: currentPage === index + 1 ? '#2d5f3f' : 'white',
                      color: currentPage === index + 1 ? 'white' : '#2d5f3f',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: currentPage === index + 1 ? 'bold' : 'normal'
                    }}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              <button 
                className="btn btn-primary" 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{opacity: currentPage === totalPages ? 0.5 : 1}}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {selectedStudentId && (
        <StudentDetailModal 
          studentId={selectedStudentId}
          onClose={() => setSelectedStudentId(null)}
          onUpdate={loadStudents}
        />
      )}
    </div>
  );
}

export default StudentsPage;
